import { createApp } from 'vue'
import './style.css'
import App from './App.vue'

import { VueQueryPlugin } from '@tanstack/vue-query'
import type { VueQueryPluginOptions } from '@tanstack/vue-query'
import pinia from './stores'
import i18n from './i18n'
import PrimeVue from 'primevue/config'
import { definePreset } from '@primeuix/themes'
import Aura from '@primeuix/themes/aura'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import router from './router'

import { fr } from 'primelocale/js/fr.js'

const app = createApp(App)

const vueQueryPluginOptions: VueQueryPluginOptions = {
  enableDevtoolsV6Plugin: true,
}
app.use(VueQueryPlugin, vueQueryPluginOptions)
app.use(pinia)
app.use(i18n)
app.use(PrimeVue, {
  theme: {
    preset: definePreset(Aura, {
      primitive: {
        emerald: {
          50: '#EDF0F8',
          100: '#CBD6EB',
          200: '#AABCDF',
          300: '#89A1D2',
          400: '#6787C5',
          500: '#4265AD',
          600: '#3A5998',
          700: '#2D4576',
          800: '#203255',
          900: '#141E34',
          950: '#070B12',
        },
        green: {
          50: '#ECF9F3',
          100: '#C9EDDC',
          200: '#A7E2C6',
          300: '#85D6B0',
          400: '#62CB9A',
          500: '#40BF84',
          600: '#349D6C',
          700: '#2A7F58', // Tartan
          800: '#1D583D',
          900: '#123625',
          950: '#06130D',
        },
        sky: {
          50: '#EAF7FA',
          100: '#C5E9F1',
          200: '#A0DBE9',
          300: '#7BCDE0',
          400: '#5CC2D9', //holographic
          500: '#30B2CF',
          600: '#2892A9',
          700: '#1F7284',
          800: '#16525F',
          900: '#0E323A',
          950: '#051215',
        },
        orange: {
          50: '#FFF7E6',
          100: '#FFEAB8',
          200: '#FFDC8A',
          300: '#FFD46D', // savana
          400: '#FFC02E',
          500: '#FFB300',
          600: '#D19200',
          700: '#A37200',
          800: '#755200',
          900: '#473200',
          950: '#1A1200',
        },
        purple: {
          50: '#F5EEF6',
          100: '#E3D1E6',
          200: '#D2B4D7',
          300: '#BF95C6',
          400: '#AC77B6',
          500: '#9A59A6',
          600: '#7F4988',
          700: '#63396A',
          800: '#47294C',
          900: '#2B192E',
          950: '#0F0911',
        },
        red: {
          50: '#FBEAEA',
          100: '#F4C3C4',
          200: '#EC9C9E',
          300: '#E57678',
          400: '#DE4F52',
          500: '#D6292C',
          600: '#AD2123',
          700: '#891A1C',
          800: '#631314',
          900: '#3C0B0C',
          950: '#150404',
        },
        // danger: var(--color-circus),
        // primary: var(--color-furing),
        // secondary: var(--color-allu),
        // success: var(--color-tartan),
        // info: var(--color-holographic),
        // warn: var(--color-savana),
      },
      semantic: {
        colorScheme: {
          light: {
            semantic: {
              danger: '{red.500}', // ~circus
              warn: '{orange.500}', // ~savana
              info: '{sky.500}', //   ~emerald
              success: '{green.500}', // ~tartan
              dev: {
                background: '{emerald.100}',
                orientation: '33deg',
              },
            },
          },
          dark: {
            semantic: {
              danger: '{red.400}', // ~circus
              warn: '{orange.400}', // ~savana
              info: '{sky.400}', //   ~emerald
              success: '{green.400}', // ~tartan
              dev: {
                background: '{emerald.800}',
                orientation: '-33deg',
              },
            },
          },
        },
      },
    }),

    options: {
      darkModeSelector: '.my-app-dark',
    },
  },

  locale: fr,
})

// Partie APM
const VITE_APM_SERVER_URL = import.meta.env.VITE_APM_SERVER_URL
const VITE_APM_SERVICE_NAME = import.meta.env.VITE_APM_SERVICE_NAME
const apm_enabled =(
  import.meta.env.VITE_ENV != 'dev' && VITE_APM_SERVER_URL && VITE_APM_SERVICE_NAME)
if (apm_enabled) {
  const { ApmVuePlugin } = await import('@elastic/apm-rum-vue')
  app.use(ApmVuePlugin, {
    router,
    config: {
      serviceName: VITE_APM_SERVICE_NAME || 'tbb_comm_front',
      serverUrl: VITE_APM_SERVER_URL,
    },
    captureErrors: true,
  })
  // })
} else {
  console.log('apm désactivé')
}

app.use(ToastService)
app.use(ConfirmationService)

app.use(router)

app.mount('#app')
